import React, { useEffect } from "react";
import {
    Card, CardBody, CardHeader, Row, Col, Input, Label,
    FormGroup, Button, Badge, FormFeedback
} from "reactstrap";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import { serviceSchema, serviceDefaults } from "../../../../models/service";
import { ITEM_TYPES } from "../constants";
import { OverlayLoader, LoadingButton } from "../../../../components/Common";
import { formatCurrency } from "../../../../utils/format";

/**
 * ServiceEditor
 * Formulário principal de Produtos e Serviços.
 * Segue o padrão do AreaEditor: header sticky com ações, body com formulário.
 */
const ServiceEditor = ({ item, onSave, onDelete, isSaving, onChange }) => {

    const formik = useFormik({
        initialValues: serviceDefaults,
        validationSchema: serviceSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave(values);
        },
    });

    // Sincroniza estado quando o item selecionado muda
    useEffect(() => {
        if (item) {
            formik.resetForm({
                values: {
                    id: item.id || "",
                    nameService: item.nameService || "",
                    type: item.type || "service",
                    value: item.value || 0,
                    allowEntries: item.allowEntries || false,
                    experimentalClass: item.experimentalClass || false,
                    maxAmountInstallments: item.maxAmountInstallments || 1,
                    stock: item.stock || 0,
                    costPrice: item.costPrice || 0,
                    minimumStock: item.minimumStock || 0,
                    inactive: item.inactive || false,
                    order: item.order || 0,
                    createdAt: item.createdAt || null,
                    createdBy: item.createdBy || null
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item]);

    // Notifica mudança para o pai (isDirty)
    useEffect(() => {
        if (onChange) onChange(formik.dirty);
    }, [formik.dirty, onChange]);

    // Estado vazio
    if (!item) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-package-variant-closed"></i>
                        </div>
                    </div>
                    <h5>Selecione um item</h5>
                    <p className="text-muted">Escolha um produto ou serviço na lista ao lado ou crie um novo para gerenciar seus itens de venda.</p>
                </div>
            </div>
        );
    }

    const typeBadge = formik.values.type === "product"
        ? { color: "info", label: "Produto" }
        : { color: "primary", label: "Serviço" };

    return (
        <Card className="border-0 shadow-sm overflow-hidden rounded-0 position-relative">
            {/* Header */}
            <CardHeader className="bg-white py-3 border-bottom sticky-top" style={{ zIndex: 10 }}>
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                        <div className="avatar-sm flex-shrink-0">
                            <span className="avatar-title bg-primary bg-gradient text-white rounded shadow-sm">
                                <i className={`mdi ${formik.values.type === 'product' ? 'mdi-package-variant' : 'mdi-room-service'} font-size-22`}></i>
                            </span>
                        </div>
                        <div className="ms-3 overflow-hidden">
                            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                                <span className="text-truncate">{formik.values.id ? formik.values.nameService : "Novo Item"}</span>
                                {formik.values.id && (
                                    <>
                                        <Badge color={typeBadge.color} pill className="font-size-10 px-2 flex-shrink-0">
                                            {typeBadge.label}
                                        </Badge>
                                        {formik.values.inactive && (
                                            <Badge color="danger" pill className="font-size-10 px-2 flex-shrink-0">
                                                Inativo
                                            </Badge>
                                        )}
                                    </>
                                )}
                            </h5>
                            <p className="text-muted mb-0 small text-truncate">
                                {formik.values.id ? formatCurrency(formik.values.value) : "Preencha os dados do item"}
                            </p>
                        </div>
                    </div>
                    <div className="d-flex gap-2 flex-shrink-0">
                        {formik.values.id && (
                            <Button
                                color="light"
                                className="btn-md rounded-0 px-2 shadow-none text-danger border-0 d-flex align-items-center justify-content-center"
                                style={{ width: '38px', height: '38px' }}
                                onClick={() => onDelete(formik.values.id, formik.values.nameService)}
                                disabled={isSaving}
                                title="Excluir Item"
                            >
                                <i className="mdi mdi-trash-can-outline font-size-18"></i>
                            </Button>
                        )}
                        <LoadingButton
                            color="primary"
                            className="btn-md rounded-0 px-4 shadow-sm"
                            onClick={formik.handleSubmit}
                            loading={isSaving}
                            loadingText="Salvando..."
                        >
                            Salvar
                        </LoadingButton>
                    </div>
                </div>
            </CardHeader>

            <CardBody className="p-4">
                <OverlayLoader show={isSaving} label="Salvando..." />

                {/* Dados Principais */}
                <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                    <i className="mdi mdi-information-outline me-2 font-size-16 text-primary"></i>
                    Dados Principais
                </h6>
                <Row className="mb-4">
                    <Col md="6">
                        <FormGroup className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase">Nome do Item</Label>
                            <Input
                                name="nameService"
                                type="text"
                                className="form-control bg-light-subtle border-0"
                                placeholder="Ex: Taxa de Adesão, Garrafinha, etc."
                                {...formik.getFieldProps("nameService")}
                                invalid={formik.touched.nameService && !!formik.errors.nameService}
                            />
                            <FormFeedback>{formik.errors.nameService}</FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md="3">
                        <FormGroup className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase">Tipo</Label>
                            <Input
                                type="select"
                                name="type"
                                className="form-control bg-light-subtle border-0"
                                {...formik.getFieldProps("type")}
                            >
                                {ITEM_TYPES.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </Input>
                        </FormGroup>
                    </Col>
                    <Col md="3">
                        <FormGroup className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase">Valor (R$)</Label>
                            <Input
                                name="value"
                                type="number"
                                className="form-control bg-light-subtle border-0"
                                {...formik.getFieldProps("value")}
                                invalid={formik.touched.value && !!formik.errors.value}
                            />
                            <FormFeedback>{formik.errors.value}</FormFeedback>
                        </FormGroup>
                    </Col>
                </Row>

                {/* Parcelas */}
                <Row className="mb-4">
                    <Col md="3">
                        <FormGroup className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase">Parc. Máximas</Label>
                            <Input
                                name="maxAmountInstallments"
                                type="number"
                                className="form-control bg-light-subtle border-0"
                                {...formik.getFieldProps("maxAmountInstallments")}
                                invalid={formik.touched.maxAmountInstallments && !!formik.errors.maxAmountInstallments}
                            />
                            <FormFeedback>{formik.errors.maxAmountInstallments}</FormFeedback>
                        </FormGroup>
                    </Col>
                </Row>

                {/* Estoque e Custos — Só para Produtos */}
                {formik.values.type === "product" && (
                    <>
                        <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                            <i className="mdi mdi-package-variant me-2 font-size-16 text-primary"></i>
                            Controle de Estoque e Custos
                        </h6>
                        <Row className="mb-4">
                            <Col md="4">
                                <FormGroup className="mb-3">
                                    <Label className="fw-medium text-muted small text-uppercase">Valor de Custo (R$)</Label>
                                    <Input
                                        name="costPrice"
                                        type="number"
                                        className="form-control bg-light-subtle border-0"
                                        placeholder="Quanto a academia paga"
                                        {...formik.getFieldProps("costPrice")}
                                        invalid={formik.touched.costPrice && !!formik.errors.costPrice}
                                    />
                                    <FormFeedback>{formik.errors.costPrice}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup className="mb-3">
                                    <Label className="fw-medium text-muted small text-uppercase">Estoque Atual Base</Label>
                                    <Input
                                        name="stock"
                                        type="number"
                                        className="form-control bg-light-subtle border-0"
                                        placeholder="Itens em estoque"
                                        {...formik.getFieldProps("stock")}
                                        invalid={formik.touched.stock && !!formik.errors.stock}
                                    />
                                    <FormFeedback>{formik.errors.stock}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup className="mb-3">
                                    <Label className="fw-medium text-muted small text-uppercase">Alerta de Estoque Mínimo</Label>
                                    <Input
                                        name="minimumStock"
                                        type="number"
                                        className="form-control bg-light-subtle border-0"
                                        placeholder="Avisar em X itens"
                                        {...formik.getFieldProps("minimumStock")}
                                        invalid={formik.touched.minimumStock && !!formik.errors.minimumStock}
                                    />
                                    <FormFeedback>{formik.errors.minimumStock}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>
                    </>
                )}

                {/* Configurações */}
                <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                    <i className="mdi mdi-cog-outline me-2 font-size-16 text-primary"></i>
                    Configurações
                </h6>
                <Row>
                    <Col md="4">
                        <FormGroup className="mb-3">
                            <div className="form-check form-switch pt-2">
                                <Input
                                    type="switch"
                                    role="switch"
                                    checked={formik.values.inactive === true}
                                    onChange={(e) => formik.setFieldValue("inactive", e.target.checked)}
                                    className="form-check-input custom-switch-danger"
                                    id="switch-inactive"
                                />
                                <Label className="ms-2 small fw-bold text-danger" htmlFor="switch-inactive">
                                    Item Inativo
                                </Label>
                            </div>
                            <small className="text-muted ms-5 d-block">Ocultar de todo o sistema.</small>
                        </FormGroup>
                    </Col>

                    {formik.values.type === "service" && (
                        <>
                            <Col md="4">
                                <FormGroup className="mb-3">
                                    <div className="form-check form-switch pt-2">
                                        <Input
                                            type="switch"
                                            role="switch"
                                            checked={formik.values.allowEntries === true}
                                            onChange={(e) => formik.setFieldValue("allowEntries", e.target.checked)}
                                            className="form-check-input custom-switch-primary"
                                            id="switch-entries"
                                        />
                                        <Label className="ms-2 small fw-bold" htmlFor="switch-entries">
                                            Permite Entradas (Acesso)
                                        </Label>
                                    </div>
                                    <small className="text-muted ms-5 d-block">Liberar catraca/leitora na compra.</small>
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup className="mb-3">
                                    <div className="form-check form-switch pt-2">
                                        <Input
                                            type="switch"
                                            role="switch"
                                            checked={formik.values.experimentalClass === true}
                                            onChange={(e) => formik.setFieldValue("experimentalClass", e.target.checked)}
                                            className="form-check-input custom-switch-primary"
                                            id="switch-experimental"
                                        />
                                        <Label className="ms-2 small fw-bold" htmlFor="switch-experimental">
                                            Vincular a Aula Experimental
                                        </Label>
                                    </div>
                                </FormGroup>
                            </Col>
                        </>
                    )}
                </Row>
            </CardBody>
        </Card>
    );
};

ServiceEditor.propTypes = {
    item: PropTypes.object,
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default ServiceEditor;
